import type { RequestOpts } from '@oazapfts/runtime';
import { Err, Ok } from 'ts-results-es';

import {
  alertsV1NotifyConfigsDeleteDestroy,
  alertsV1NotifyConfigsListRetrieve,
  alertsV1NotifyConfigsNewCreate,
  alertsV1NotifyConfigsUpdateUpdate,
} from '@/api/fe-api/__generated__/oazapfts-client';
import { appConfig } from '@/config';
import type { Auth } from '@/entities/auth';
import type { Me } from '@/entities/me';

import type { IAlertNotificationRepository } from '../domain/repositories/alert-notification-respository';
import type { RequestOptions } from '../domain/repositories/utils/request-options';
import { handle } from '../helpers/handle';
import { AlertNotificationRepositoryMapper } from '../mappers/alert-notification.repository.mapper';

export class FeApiAlertNotificationRepository implements IAlertNotificationRepository {
  constructor(
    private me: Me,
    private auth: Auth,
  ) {}

  private async getOpts(): Promise<RequestOpts> {
    const accessToken = await this.auth.getAccessToken();

    return {
      baseUrl: `${appConfig.bffUrl}/plex`,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'x-user-email': this.me.email,
        'x-user-name': this.me.displayName,
      },
    };
  }

  async getAll(
    _: IAlertNotificationRepository.GetAllInput,
    requestOptions?: RequestOptions,
  ): Promise<IAlertNotificationRepository.GetAllOutput> {
    const opts = await this.getOpts();
    const response = await alertsV1NotifyConfigsListRetrieve(
      {},
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<IAlertNotificationRepository.GetAllOutput>({
      200(data) {
        const items =
          AlertNotificationRepositoryMapper.getAll.outputFromData(data);

        return items;
      },
    });
  }

  async create(
    input: IAlertNotificationRepository.CreateInput,
    requestOptions?: RequestOptions,
  ): Promise<IAlertNotificationRepository.CreateOutput> {
    const opts = await this.getOpts();
    const response = await alertsV1NotifyConfigsNewCreate(
      AlertNotificationRepositoryMapper.create.bodyFromInput(input),
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<IAlertNotificationRepository.CreateOutput>({
      201() {
        return Ok({ id: input.description ?? '' });
      },
      400() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
      422() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
    });
  }

  async update(
    input: IAlertNotificationRepository.UpdateInput,
    requestOptions?: RequestOptions,
  ): Promise<IAlertNotificationRepository.UpdateOutput> {
    const opts = await this.getOpts();
    const response = await alertsV1NotifyConfigsUpdateUpdate(
      AlertNotificationRepositoryMapper.updateById.bodyFromInput(input),
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<IAlertNotificationRepository.UpdateOutput>({
      200() {
        return Ok(undefined);
      },
      400() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
      422() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
    });
  }

  async delete(
    input: IAlertNotificationRepository.DeleteInput,
    requestOptions?: RequestOptions,
  ): Promise<IAlertNotificationRepository.DeleteOutput> {
    const opts = await this.getOpts();
    const response = await alertsV1NotifyConfigsDeleteDestroy(
      AlertNotificationRepositoryMapper.deleteById.bodyFromInput(input),
      {
        ...opts,
        signal: requestOptions?.signal,
      },
    );

    return handle(response).to<IAlertNotificationRepository.DeleteOutput>({
      200() {
        return Ok(undefined);
      },
      400() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
      422() {
        return Err({ type: 'EXTERNAL_ERROR', description: undefined });
      },
    });
  }
}
